import { BrickWall, RectangleVertical, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { ApartmentAverageForProject } from '@obraplan/core/application/use-cases/takeoff/rate-average-use-cases';
import type { StructuralPieceId } from '@obraplan/core/domain/shared/ids';
import {
  nomeDaPeca,
  pecasImportadas,
  type QuantidadesPorPeca,
} from '@obraplan/core/domain/takeoff/planilha-por-peca';
import {
  retainingColumnFaces,
  retainingColumnVolume,
  retainingWallFace,
  retainingWallLength,
  retainingWallVolume,
  type RetainingColumn,
  type RetainingWall,
} from '@obraplan/core/domain/takeoff/retaining';
import type { ServiceCatalog } from '@obraplan/core/domain/takeoff/service-catalog';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/input';
import { NOME_DA_PECA_MAX, NomeInput, NumeroInput } from './campos';
import {
  cantosDoPilar,
  COR_DO_MURO,
  COR_DO_PILAR_DE_CONTENCAO,
  encaixarNoMuro,
  pilarSolto,
  rotuloDoMuro,
  secaoDoPilarDeContencao,
} from './contencao-da-prancha';
import {
  acoAprovadoDoElemento,
  fatoresDaPecaDaFundacao,
  mediaDoAcoDaPeca,
  taxaDeBaseDaPeca,
} from './fatores-proprios';
import {
  AcoDaPecaDaFundacao,
  FatoresDaPecaDaFundacao,
  Linha,
  NumeroAoTerminar,
} from './FundacaoNaPrancha';
import { faixaDoEixo, pontosDe } from './fundacao-da-prancha';
import { metros } from './formato';
import { metresToPixel, pixelToMetres } from './plan-tracer';
import type { ServicosAprovados } from './servicos-aprovados';
import type {
  ArrastoDaContencao,
  ContencaoNaPrancha,
  FerramentaDaContencao,
  PecaDaContencao,
} from './use-contencao-na-prancha';
import type { WorkspaceAction } from './typology-draft';

/**
 * O muro e o pilar de contenção sobre a prancha, e os painéis deles (Fase 27, etapa 4).
 *
 * O muro é a faixa **na espessura** ao longo do eixo, com o nome e a face em elevação no
 * rótulo. O pilar é o retângulo da seção, girado no ângulo do muro em que está encaixado, com
 * o nome — e "solto" quando não cai sobre muro nenhum (RN-372). As cores não seguem o tema,
 * pelo mesmo motivo das do resto do traçado: a prancha é papel branco.
 */

// ── O desenho ───────────────────────────────────────────────────────────────────

/** Um muro: a faixa na espessura, com o rótulo da face. */
function MuroNaPrancha({
  muro,
  pilares,
  escala,
  zoom,
  selecionado,
  novo,
}: {
  muro: Pick<RetainingWall, 'name' | 'from' | 'to' | 'height' | 'thickness'> & { id?: string };
  pilares: readonly RetainingColumn[];
  escala: number;
  zoom: number;
  selecionado: boolean;
  /** O muro ainda não cadastrado: tracejado, e sem alvo para clique. */
  novo: boolean;
}) {
  const de = metresToPixel(muro.from, escala);
  const ate = metresToPixel(muro.to, escala);
  const largura = Math.max(muro.thickness / escala, 3 / zoom);
  const faixa = faixaDoEixo(de, ate, largura);
  const meio = { x: (de.x + ate.x) / 2, y: (de.y + ate.y) / 2 };
  const fonte = 11 / zoom;

  return (
    <g
      data-muro={novo ? undefined : muro.id}
      data-muro-novo={novo ? '' : undefined}
      className={novo ? 'pointer-events-none' : undefined}
    >
      <title>
        {`Muro ${muro.name} — ${metros(retainingWallLength(muro))} m × ${metros(muro.height)} m, espessura ${String(
          Math.round(muro.thickness * 100),
        )} cm`}
      </title>
      {/* O alvo do clique: mais largo que a faixa, que numa espessura de 20 cm é fina demais. */}
      <line
        x1={de.x}
        y1={de.y}
        x2={ate.x}
        y2={ate.y}
        stroke="transparent"
        strokeWidth={Math.max(largura, 12 / zoom)}
        pointerEvents="stroke"
      />
      <polygon
        points={pontosDe(faixa)}
        fill={COR_DO_MURO}
        fillOpacity={novo ? 0.2 : 0.35}
        stroke={COR_DO_MURO}
        strokeWidth={(selecionado ? 3 : 1.5) / zoom}
        strokeDasharray={novo ? `${4 / zoom} ${3 / zoom}` : undefined}
        strokeLinejoin="round"
        pointerEvents="none"
      />
      <text
        data-rotulo=""
        x={meio.x}
        y={meio.y - largura / 2 - 4 / zoom}
        textAnchor="middle"
        fontSize={fonte}
        fontWeight={600}
        fill="#78350f"
        stroke="#ffffff"
        strokeWidth={fonte * 0.22}
        paintOrder="stroke"
        className="pointer-events-none select-none"
      >
        {rotuloDoMuro(muro, pilares)}
      </text>
    </g>
  );
}

/**
 * As alças das duas pontas do muro escolhido, para arrastar. Desenhadas **depois dos pilares**:
 * um pilar encaixado na ponta cobriria a alça, e o aperto cairia nele, não nela.
 */
function AlcasDoMuro({
  muro,
  escala,
  zoom,
}: {
  muro: Pick<RetainingWall, 'id' | 'from' | 'to'>;
  escala: number;
  zoom: number;
}) {
  return (
    <>
      {(['de', 'ate'] as const).map((qual) => {
        const lugar = metresToPixel(qual === 'de' ? muro.from : muro.to, escala);
        return (
          <circle
            key={qual}
            data-muro-ponta={muro.id}
            data-ponta={qual}
            cx={lugar.x}
            cy={lugar.y}
            r={6 / zoom}
            fill="#ffffff"
            stroke={COR_DO_MURO}
            strokeWidth={2 / zoom}
            className="cursor-move"
          />
        );
      })}
    </>
  );
}

/** O muro com a ponta na mão no lugar por onde o mouse passa. */
function muroNaMao(
  muro: RetainingWall,
  arrastando: ArrastoDaContencao | undefined,
  escala: number,
): RetainingWall {
  if (arrastando?.tipo !== 'PONTA' || arrastando.id !== muro.id || !arrastando.moveu) return muro;
  const solta = pixelToMetres(arrastando.atual, escala);
  return arrastando.ponta === 'de' ? { ...muro, from: solta } : { ...muro, to: solta };
}

/** Um pilar: o retângulo da seção no ângulo dele, com o nome — e o aviso, se estiver solto. */
function PilarNaPrancha({
  pilar,
  escala,
  zoom,
  solto,
  selecionado,
  novo,
}: {
  pilar: Pick<
    RetainingColumn,
    'name' | 'center' | 'sectionWidth' | 'sectionDepth' | 'height' | 'angle'
  > & { id?: string };
  escala: number;
  zoom: number;
  solto: boolean;
  selecionado: boolean;
  /** O pilar ainda não cadastrado: tracejado, e sem alvo para clique. */
  novo: boolean;
}) {
  const centro = metresToPixel(pilar.center, escala);
  // A seção, em pixel, nunca menor que um ponto que se enxergue.
  const largura = Math.max(pilar.sectionWidth / escala, 4 / zoom);
  const profundidade = Math.max(pilar.sectionDepth / escala, 4 / zoom);
  const cantos = cantosDoPilar(centro, largura, profundidade, pilar.angle);
  const fonte = 11 / zoom;
  const alto = Math.max(largura, profundidade) / 2;

  return (
    <g
      data-pilar-contencao={novo ? undefined : pilar.id}
      data-pilar-contencao-novo={novo ? '' : undefined}
      data-solto={solto ? '' : undefined}
      className={novo ? 'pointer-events-none' : undefined}
    >
      <title>
        {`Pilar de contenção ${pilar.name} — ${secaoDoPilarDeContencao(pilar)} cm × ${metros(
          pilar.height,
        )} m${solto ? ' · solto: não está sobre nenhum muro' : ''}`}
      </title>
      <polygon
        points={pontosDe(cantos)}
        fill={COR_DO_PILAR_DE_CONTENCAO}
        fillOpacity={novo ? 0.25 : 0.55}
        stroke={COR_DO_PILAR_DE_CONTENCAO}
        strokeWidth={(selecionado ? 3.5 : 1.5) / zoom}
        strokeDasharray={novo || solto ? `${3 / zoom} ${2 / zoom}` : undefined}
        strokeLinejoin="round"
      />
      <text
        data-rotulo=""
        x={centro.x}
        y={centro.y + alto + fonte + 2 / zoom}
        textAnchor="middle"
        fontSize={fonte}
        fontWeight={600}
        fill="#881337"
        stroke="#ffffff"
        strokeWidth={fonte * 0.22}
        paintOrder="stroke"
        className="pointer-events-none select-none"
      >
        {solto ? `${pilar.name} · solto` : pilar.name}
      </text>
    </g>
  );
}

export function ContencaoDesenhada({
  muros,
  pilares,
  escala,
  zoom,
  selecionada,
  arrastando,
  clicaveis,
  onClicarMuro,
  onClicarPilar,
}: {
  muros: readonly RetainingWall[];
  pilares: readonly RetainingColumn[];
  escala: number;
  zoom: number;
  selecionada: PecaDaContencao | undefined;
  arrastando: ArrastoDaContencao | undefined;
  /** Ausente enquanto se traça: aí o clique é do traço, e a peça deixa passar. */
  clicaveis: boolean;
  onClicarMuro: (id: StructuralPieceId) => void;
  onClicarPilar: (id: StructuralPieceId) => void;
}) {
  if (muros.length === 0 && pilares.length === 0) return null;

  return (
    <g>
      <g className={clicaveis ? 'cursor-pointer' : 'pointer-events-none'}>
        {muros.map((muro) => {
          const escolhido = selecionada?.tipo === 'MURO' && selecionada.id === muro.id;
          // A ponta na mão anda com o mouse.
          const quando = muroNaMao(muro, arrastando, escala);

          return (
            <g
              key={muro.id}
              onClick={(evento) => {
                if (!clicaveis) return;
                evento.stopPropagation();
                onClicarMuro(muro.id);
              }}
            >
              <MuroNaPrancha
                muro={quando}
                pilares={pilares}
                escala={escala}
                zoom={zoom}
                selecionado={escolhido}
                novo={false}
              />
            </g>
          );
        })}
      </g>

      {/* Os pilares por cima do muro, que os recebe: continuam clicáveis onde o muro os cobre. */}
      <g className={clicaveis ? 'cursor-pointer' : 'pointer-events-none'}>
        {pilares.map((pilar) => {
          const escolhido = selecionada?.tipo === 'PILAR_CONTENCAO' && selecionada.id === pilar.id;
          // O pilar na mão anda com o mouse, e o encaixe no muro já aparece.
          const naMao =
            arrastando?.tipo === 'PILAR_CONTENCAO' && arrastando.id === pilar.id && arrastando.moveu
              ? arrastando
              : undefined;
          const quando: RetainingColumn =
            naMao === undefined
              ? pilar
              : (() => {
                  const destino = encaixarNoMuro(
                    {
                      x: pilar.center.x + (naMao.atual.x - naMao.origem.x) * escala,
                      y: pilar.center.y + (naMao.atual.y - naMao.origem.y) * escala,
                    },
                    muros,
                    pilar.angle,
                  );
                  return { ...pilar, center: destino.centro, angle: destino.angulo };
                })();

          return (
            <g
              key={pilar.id}
              onClick={(evento) => {
                if (!clicaveis) return;
                evento.stopPropagation();
                onClicarPilar(pilar.id);
              }}
            >
              <PilarNaPrancha
                pilar={quando}
                escala={escala}
                zoom={zoom}
                solto={pilarSolto(quando, muros)}
                selecionado={escolhido}
                novo={false}
              />
            </g>
          );
        })}
      </g>

      {/* As alças por cima de tudo: o pilar encaixado na ponta não pode cobri-las. */}
      {clicaveis && selecionada?.tipo === 'MURO' && (
        <g onClick={(evento) => evento.stopPropagation()}>
          {muros
            .filter((muro) => muro.id === selecionada.id)
            .map((muro) => (
              <AlcasDoMuro
                key={muro.id}
                muro={muroNaMao(muro, arrastando, escala)}
                escala={escala}
                zoom={zoom}
              />
            ))}
        </g>
      )}
    </g>
  );
}

/** O que está sendo traçado e ainda não foi cadastrado: o eixo em faixa, o pilar tracejado. */
export function ContencaoEmTraco({
  contencao,
  escala,
  zoom,
}: {
  contencao: ContencaoNaPrancha;
  escala: number;
  zoom: number;
}) {
  const { eixo, cursor, eixoEmMetros } = contencao;

  if (contencao.ferramenta === 'MURO' && eixo.length > 0) {
    const primeiro = eixo[0]!;
    return (
      <g className="pointer-events-none">
        {eixoEmMetros !== undefined ? (
          <MuroNaPrancha
            muro={{
              name: contencao.nomeDoMuro,
              from: eixoEmMetros.from,
              to: eixoEmMetros.to,
              height: contencao.alturaDoMuro,
              thickness: contencao.espessuraEmCm / 100,
            }}
            pilares={contencao.pilares}
            escala={escala}
            zoom={zoom}
            selecionado={false}
            novo
          />
        ) : (
          cursor !== undefined && (
            <line
              data-muro-novo=""
              x1={primeiro.x}
              y1={primeiro.y}
              x2={cursor.x}
              y2={cursor.y}
              stroke={COR_DO_MURO}
              strokeWidth={2 / zoom}
              strokeDasharray={`${4 / zoom} ${3 / zoom}`}
            />
          )
        )}
        {eixo.map((ponto, indice) => (
          <circle
            key={`e-${String(indice)}`}
            cx={ponto.x}
            cy={ponto.y}
            r={5 / zoom}
            fill={COR_DO_MURO}
          />
        ))}
      </g>
    );
  }

  if (
    contencao.ferramenta === 'PILAR_CONTENCAO' &&
    contencao.centroDoPilar !== undefined &&
    contencao.encaixeDoPilar !== undefined
  ) {
    return (
      <g className="pointer-events-none">
        <PilarNaPrancha
          pilar={{
            name: contencao.nomeDoPilar,
            center: contencao.encaixeDoPilar.centro,
            sectionWidth: contencao.larguraEmCm / 100,
            sectionDepth: contencao.profundidadeEmCm / 100,
            height: contencao.alturaDoPilar,
            angle: contencao.encaixeDoPilar.angulo,
          }}
          escala={escala}
          zoom={zoom}
          solto={contencao.encaixeDoPilar.muro === undefined}
          selecionado={false}
          novo
        />
      </g>
    );
  }

  return null;
}

// ── Os botões da barra ──────────────────────────────────────────────────────────

/** "Muro" e "Pilar de contenção": moram onde a estaca mora — na Fundação e na Contenção. */
export function BotoesDaContencao({
  ferramenta,
  onEscolher,
}: {
  ferramenta: FerramentaDaContencao | undefined;
  onEscolher: (ferramenta: FerramentaDaContencao) => void;
}) {
  return (
    <>
      <Button
        size="sm"
        variant={ferramenta === 'MURO' ? 'primary' : 'ghost'}
        onClick={() => onEscolher('MURO')}
      >
        <BrickWall className="size-4" /> Muro
      </Button>
      <Button
        size="sm"
        variant={ferramenta === 'PILAR_CONTENCAO' ? 'primary' : 'ghost'}
        onClick={() => onEscolher('PILAR_CONTENCAO')}
      >
        <RectangleVertical className="size-4" /> Pilar de contenção
      </Button>
    </>
  );
}

// ── Os painéis da ferramenta ────────────────────────────────────────────────────

export function EmMuro({
  contencao,
  erro,
  onSair,
}: {
  contencao: ContencaoNaPrancha;
  erro: string | undefined;
  onSair: () => void;
}) {
  const { eixoEmMetros } = contencao;
  const comprimento = eixoEmMetros === undefined ? 0 : retainingWallLength(eixoEmMetros);

  return (
    <section
      aria-label="Muros"
      className="flex flex-col gap-3 rounded-md border border-(--color-brand) p-3"
    >
      <h3 className="text-sm font-semibold">Muro de contenção</h3>
      <p className="text-xs text-(--color-content-muted)">
        Clique nas duas pontas do eixo do muro. Os pilares de contenção se encaixam sobre ele e
        tiram do muro o que ocupam.
      </p>

      <div className="flex flex-wrap items-end gap-2">
        <Field label="Nome do muro" htmlFor="muro-nome">
          <Input
            id="muro-nome"
            maxLength={NOME_DA_PECA_MAX}
            value={contencao.nomeDoMuro}
            className="w-28"
            onChange={(evento) => contencao.digitarNomeDoMuro(evento.target.value)}
            onKeyDown={(evento) => {
              if (evento.key === 'Enter') contencao.adicionarMuro();
            }}
          />
        </Field>
        <Field label="Altura do muro (m)" htmlFor="muro-altura">
          <NumeroInput
            id="muro-altura"
            value={contencao.alturaDoMuro}
            className="w-24"
            onChange={contencao.mudarAlturaDoMuro}
          />
        </Field>
        <Field label="Espessura do muro (cm)" htmlFor="muro-espessura">
          <NumeroInput
            id="muro-espessura"
            value={contencao.espessuraEmCm}
            className="w-24"
            onChange={contencao.mudarEspessuraEmCm}
          />
        </Field>
      </div>

      <span className="text-xs text-(--color-content-muted)">
        {eixoEmMetros === undefined
          ? `${String(contencao.eixo.length)} de 2 pontos`
          : `Eixo ${metros(comprimento)} m · face ${metros(
              retainingWallFace({
                ...eixoEmMetros,
                height: contencao.alturaDoMuro,
                thickness: contencao.espessuraEmCm / 100,
              }),
            )} m²`}
      </span>
      {contencao.impedimentoDoMuro !== undefined && (
        <span className="text-xs text-(--color-state-warn)">{contencao.impedimentoDoMuro}</span>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          variant="primary"
          size="sm"
          disabled={eixoEmMetros === undefined || contencao.impedimentoDoMuro !== undefined}
          onClick={contencao.adicionarMuro}
        >
          Adicionar muro
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={contencao.eixo.length === 0}
          onClick={contencao.refazerMuro}
        >
          Refazer
        </Button>
      </div>

      {erro !== undefined && (
        <p role="alert" className="text-xs text-(--color-state-danger)">
          {erro}
        </p>
      )}

      <div className="border-t border-(--color-border-subtle) pt-2">
        <Button variant="ghost" size="sm" onClick={onSair}>
          Sair
        </Button>
      </div>
    </section>
  );
}

export function EmPilarDeContencao({
  contencao,
  erro,
  onSair,
}: {
  contencao: ContencaoNaPrancha;
  erro: string | undefined;
  onSair: () => void;
}) {
  const { encaixeDoPilar } = contencao;

  return (
    <section
      aria-label="Pilares de contenção"
      className="flex flex-col gap-3 rounded-md border border-(--color-brand) p-3"
    >
      <h3 className="text-sm font-semibold">Pilar de contenção</h3>
      <p className="text-xs text-(--color-content-muted)">
        Clique no centro do pilar. Sobre um muro, ele vai para o eixo e copia o ângulo do muro; a
        altura sugerida é a do muro.
      </p>

      <div className="flex flex-wrap items-end gap-2">
        <Field label="Nome do pilar de contenção" htmlFor="pilar-contencao-nome">
          <Input
            id="pilar-contencao-nome"
            maxLength={NOME_DA_PECA_MAX}
            value={contencao.nomeDoPilar}
            className="w-28"
            onChange={(evento) => contencao.digitarNomeDoPilar(evento.target.value)}
            onKeyDown={(evento) => {
              if (evento.key === 'Enter') contencao.adicionarPilar();
            }}
          />
        </Field>
        <Field label="Largura do pilar (cm)" htmlFor="pilar-contencao-largura">
          <NumeroInput
            id="pilar-contencao-largura"
            value={contencao.larguraEmCm}
            className="w-24"
            onChange={contencao.mudarLarguraEmCm}
          />
        </Field>
        <Field label="Profundidade do pilar (cm)" htmlFor="pilar-contencao-profundidade">
          <NumeroInput
            id="pilar-contencao-profundidade"
            value={contencao.profundidadeEmCm}
            className="w-24"
            onChange={contencao.mudarProfundidadeEmCm}
          />
        </Field>
        <Field label="Altura do pilar (m)" htmlFor="pilar-contencao-altura">
          <NumeroInput
            id="pilar-contencao-altura"
            value={contencao.alturaDoPilar}
            className="w-24"
            onChange={contencao.mudarAlturaDoPilar}
          />
        </Field>
      </div>

      <span className="text-xs text-(--color-content-muted)">
        {encaixeDoPilar === undefined
          ? 'Nenhum ponto marcado na planta.'
          : encaixeDoPilar.muro === undefined
            ? `Seção ${secaoDoPilarDeContencao({
                sectionWidth: contencao.larguraEmCm / 100,
                sectionDepth: contencao.profundidadeEmCm / 100,
              })} cm · solto: fora de qualquer muro`
            : `Seção ${secaoDoPilarDeContencao({
                sectionWidth: contencao.larguraEmCm / 100,
                sectionDepth: contencao.profundidadeEmCm / 100,
              })} cm · encaixado no muro ${encaixeDoPilar.muro.name} · ângulo ${metros(
                encaixeDoPilar.angulo,
                1,
              )}°`}
      </span>
      {contencao.impedimentoDoPilar !== undefined && (
        <span className="text-xs text-(--color-state-warn)">{contencao.impedimentoDoPilar}</span>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          variant="primary"
          size="sm"
          disabled={
            contencao.centroDoPilar === undefined || contencao.impedimentoDoPilar !== undefined
          }
          onClick={contencao.adicionarPilar}
        >
          Adicionar pilar de contenção
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={contencao.centroDoPilar === undefined}
          onClick={contencao.refazerPilar}
        >
          Refazer
        </Button>
      </div>

      {erro !== undefined && (
        <p role="alert" className="text-xs text-(--color-state-danger)">
          {erro}
        </p>
      )}

      <div className="border-t border-(--color-border-subtle) pt-2">
        <Button variant="ghost" size="sm" onClick={onSair}>
          Sair
        </Button>
      </div>
    </section>
  );
}

// ── A peça selecionada ──────────────────────────────────────────────────────────

/** Metros em centímetros, com uma casa no máximo — "0,3" vira 30, e não 30,000000000000004. */
const emCentimetros = (valor: number): number => Math.round(valor * 1000) / 10;

/**
 * Editar o muro clicado: o nome, a altura e a espessura, com as contas à vista, e excluir.
 *
 * Desde a Fase 42 (etapa 3 — G3; RN-571, RN-572, RN-574), o card traz também "Fatores deste
 * muro" e "Aço deste muro (kg/m³)", os mesmos recolhíveis do baldrame: sem tipo, o fator próprio
 * vale por cima do índice da obra, e a taxa própria por cima da da disciplina — a lista do muro,
 * quando a PILARES DE CONTENÇÃO a tem (RN-573). O muro da planilha por peça não leva nenhum dos
 * dois, e o card diz por quê.
 *
 * Com a média da estrutura (etapa 4 — RN-584), cada linha de fator e cada bitola mostram a média
 * de hoje ao lado — a do aço do muro, separada da do pilar (RN-580) —, só para ler.
 */
export function MuroSelecionado({
  muro,
  pilares,
  erro,
  catalogo,
  pecasDaPlanilha,
  aprovados,
  media,
  dispatch: despachar,
  onFechar,
}: {
  muro: RetainingWall;
  pilares: readonly RetainingColumn[];
  /** A última recusa do desenho — só aparece depois de a pessoa editar **este** muro. */
  erro: string | undefined;
  /** O cadastro da obra, para os fatores e o aço próprios (Fase 42, RN-571). Ausente, sem eles. */
  catalogo?: Pick<ServiceCatalog, 'disciplines'> | undefined;
  /** A planilha por peça: o muro de nome importado não leva fator nem taxa (RN-574). */
  pecasDaPlanilha?: QuantidadesPorPeca | undefined;
  /** Os serviços aprovados no orçamento: o de aço do muro trava a taxa (RN-575). */
  aprovados?: ServicosAprovados | undefined;
  /** A média das obras marcadas: a da estrutura ao lado do fator e da bitola (RN-584). */
  media?: ApartmentAverageForProject | undefined;
  dispatch: React.Dispatch<WorkspaceAction>;
  onFechar: () => void;
}) {
  const [editou, setEditou] = useState(false);
  const dispatch: React.Dispatch<WorkspaceAction> = (acao) => {
    setEditou(true);
    despachar(acao);
  };
  const fatores = useMemo(
    () => fatoresDaPecaDaFundacao(catalogo, 'MURO_CONTENCAO', muro, media),
    [catalogo, muro, media],
  );
  const mediaDoAco = useMemo(() => mediaDoAcoDaPeca(media, 'MURO_CONTENCAO'), [media]);
  const daPlanilha = pecasImportadas(pecasDaPlanilha).has(nomeDaPeca(muro.name));

  return (
    <section
      aria-label={`Muro ${muro.name}`}
      className="flex flex-col gap-2 rounded-md border border-(--color-brand) p-3"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Muro {muro.name}</h3>
        <Button variant="ghost" size="sm" onClick={onFechar}>
          Fechar
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <Field label="Nome" htmlFor={`muro-${muro.id}-nome`}>
          <NomeInput
            id={`muro-${muro.id}-nome`}
            value={muro.name}
            className="w-28"
            respostaImediata
            onDescartar={() => dispatch({ type: 'DISMISS_ERROR' })}
            onCommit={(nome) =>
              dispatch({ type: 'UPDATE_RETAINING_WALL', pieceId: muro.id, name: nome })
            }
          />
        </Field>
        <Field label="Altura (m)" htmlFor={`muro-${muro.id}-altura`}>
          <NumeroAoTerminar
            id={`muro-${muro.id}-altura`}
            valor={muro.height}
            className="w-24"
            onCommit={(altura) =>
              dispatch({ type: 'UPDATE_RETAINING_WALL', pieceId: muro.id, height: altura })
            }
          />
        </Field>
        <Field label="Espessura (cm)" htmlFor={`muro-${muro.id}-espessura`}>
          <NumeroAoTerminar
            id={`muro-${muro.id}-espessura`}
            valor={emCentimetros(muro.thickness)}
            className="w-24"
            onCommit={(espessura) =>
              dispatch({
                type: 'UPDATE_RETAINING_WALL',
                pieceId: muro.id,
                thickness: espessura / 100,
              })
            }
          />
        </Field>
      </div>

      <div className="flex flex-col gap-1 text-xs">
        <Linha rotulo="Eixo, de ponta a ponta" valor={`${metros(retainingWallLength(muro))} m`} />
        <Linha
          rotulo="Face, sem os pilares"
          valor={`${metros(retainingWallFace(muro, pilares))} m²`}
        />
        <Linha
          rotulo="Concreto, sem os pilares"
          valor={`${metros(retainingWallVolume(muro, pilares), 3)} m³`}
        />
      </div>
      <p className="text-xs text-(--color-content-muted)">
        Arraste uma alça da ponta para mover só ela. Os pilares não vão junto: o que sair de cima do
        muro fica solto.
      </p>

      {catalogo !== undefined && (
        <FatoresDaPecaDaFundacao
          peca={muro}
          rotulo="muro"
          servicos={fatores}
          daPlanilha={daPlanilha}
          semFator="não leva fator próprio"
          rotuloDaVolta="Voltar ao índice"
          dispatch={dispatch}
        />
      )}
      {catalogo !== undefined && (
        <AcoDaPecaDaFundacao
          peca={muro}
          rotulo="muro"
          base={taxaDeBaseDaPeca(catalogo, 'MURO_CONTENCAO')}
          daPlanilha={daPlanilha}
          dispatch={dispatch}
          media={mediaDoAco}
          travadaPor={acoAprovadoDoElemento(aprovados, catalogo, 'MURO_CONTENCAO')}
        />
      )}

      {editou && erro !== undefined && (
        <p role="alert" className="text-xs text-(--color-state-danger)">
          {erro}
        </p>
      )}

      <div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            dispatch({ type: 'REMOVE_RETAINING_WALL', pieceId: muro.id });
            onFechar();
          }}
        >
          <Trash2 className="size-4" /> Excluir muro
        </Button>
      </div>
    </section>
  );
}

/**
 * Editar o pilar clicado: o nome, a seção e a altura, o encaixe à vista, e excluir.
 *
 * Desde a Fase 42 (etapa 3 — G3; RN-571, RN-572, RN-574), o card traz também "Fatores deste
 * pilar de contenção" e "Aço deste pilar de contenção (kg/m³)", como o muro: sem tipo, por cima
 * do índice da obra e da taxa da disciplina — a lista do pilar, quando a PILARES DE CONTENÇÃO a
 * tem (RN-573). O pilar da planilha por peça não leva nenhum dos dois, e o card diz por quê.
 *
 * Com a média da estrutura (etapa 4 — RN-584), a média de hoje ao lado, como no muro — a do aço
 * do pilar de contenção, separada da do muro (RN-580).
 */
export function PilarDeContencaoSelecionado({
  pilar,
  muros,
  erro,
  catalogo,
  pecasDaPlanilha,
  aprovados,
  media,
  dispatch: despachar,
  onFechar,
}: {
  pilar: RetainingColumn;
  muros: readonly RetainingWall[];
  /** A última recusa do desenho — só aparece depois de a pessoa editar **este** pilar. */
  erro: string | undefined;
  /** O cadastro da obra, para os fatores e o aço próprios (Fase 42, RN-571). Ausente, sem eles. */
  catalogo?: Pick<ServiceCatalog, 'disciplines'> | undefined;
  /** A planilha por peça: o pilar de nome importado não leva fator nem taxa (RN-574). */
  pecasDaPlanilha?: QuantidadesPorPeca | undefined;
  /** Os serviços aprovados no orçamento: o de aço do pilar de contenção trava a taxa (RN-575). */
  aprovados?: ServicosAprovados | undefined;
  /** A média das obras marcadas: a da estrutura ao lado do fator e da bitola (RN-584). */
  media?: ApartmentAverageForProject | undefined;
  dispatch: React.Dispatch<WorkspaceAction>;
  onFechar: () => void;
}) {
  const [editou, setEditou] = useState(false);
  const dispatch: React.Dispatch<WorkspaceAction> = (acao) => {
    setEditou(true);
    despachar(acao);
  };
  const encaixe = encaixarNoMuro(pilar.center, muros, pilar.angle);
  const fatores = useMemo(
    () => fatoresDaPecaDaFundacao(catalogo, 'PILAR_CONTENCAO', pilar, media),
    [catalogo, pilar, media],
  );
  const mediaDoAco = useMemo(() => mediaDoAcoDaPeca(media, 'PILAR_CONTENCAO'), [media]);
  const daPlanilha = pecasImportadas(pecasDaPlanilha).has(nomeDaPeca(pilar.name));

  return (
    <section
      aria-label={`Pilar de contenção ${pilar.name}`}
      className="flex flex-col gap-2 rounded-md border border-(--color-brand) p-3"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Pilar de contenção {pilar.name}</h3>
        <Button variant="ghost" size="sm" onClick={onFechar}>
          Fechar
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <Field label="Nome" htmlFor={`pilar-contencao-${pilar.id}-nome`}>
          <NomeInput
            id={`pilar-contencao-${pilar.id}-nome`}
            value={pilar.name}
            className="w-28"
            respostaImediata
            onDescartar={() => dispatch({ type: 'DISMISS_ERROR' })}
            onCommit={(nome) =>
              dispatch({ type: 'UPDATE_RETAINING_COLUMN', pieceId: pilar.id, name: nome })
            }
          />
        </Field>
        <Field label="Largura (cm)" htmlFor={`pilar-contencao-${pilar.id}-largura`}>
          <NumeroAoTerminar
            id={`pilar-contencao-${pilar.id}-largura`}
            valor={emCentimetros(pilar.sectionWidth)}
            className="w-24"
            onCommit={(largura) =>
              dispatch({
                type: 'UPDATE_RETAINING_COLUMN',
                pieceId: pilar.id,
                sectionWidth: largura / 100,
              })
            }
          />
        </Field>
        <Field label="Profundidade (cm)" htmlFor={`pilar-contencao-${pilar.id}-profundidade`}>
          <NumeroAoTerminar
            id={`pilar-contencao-${pilar.id}-profundidade`}
            valor={emCentimetros(pilar.sectionDepth)}
            className="w-24"
            onCommit={(profundidade) =>
              dispatch({
                type: 'UPDATE_RETAINING_COLUMN',
                pieceId: pilar.id,
                sectionDepth: profundidade / 100,
              })
            }
          />
        </Field>
        <Field label="Altura (m)" htmlFor={`pilar-contencao-${pilar.id}-altura`}>
          <NumeroAoTerminar
            id={`pilar-contencao-${pilar.id}-altura`}
            valor={pilar.height}
            className="w-24"
            onCommit={(altura) =>
              dispatch({
                type: 'UPDATE_RETAINING_COLUMN',
                pieceId: pilar.id,
                height: altura,
              })
            }
          />
        </Field>
      </div>

      <div className="flex flex-col gap-1 text-xs">
        <Linha rotulo="Seção" valor={`${secaoDoPilarDeContencao(pilar)} cm`} />
        <Linha
          rotulo="Encaixe"
          valor={encaixe.muro === undefined ? 'solto' : `muro ${encaixe.muro.name}`}
        />
        <Linha rotulo="Ângulo" valor={`${metros(pilar.angle, 1)}°`} />
        <Linha rotulo="Concreto" valor={`${metros(retainingColumnVolume(pilar), 3)} m³`} />
        <Linha rotulo="Fôrma" valor={`${metros(retainingColumnFaces(pilar, muros))} m²`} />
      </div>
      {encaixe.muro === undefined && (
        <p className="text-xs text-(--color-state-warn)">
          Este pilar está solto: não cai sobre nenhum muro, e por isso não tira nada dele. Arraste-o
          para cima de um muro para encaixar.
        </p>
      )}

      {catalogo !== undefined && (
        <FatoresDaPecaDaFundacao
          peca={pilar}
          rotulo="pilar de contenção"
          servicos={fatores}
          daPlanilha={daPlanilha}
          semFator="não leva fator próprio"
          rotuloDaVolta="Voltar ao índice"
          dispatch={dispatch}
        />
      )}
      {catalogo !== undefined && (
        <AcoDaPecaDaFundacao
          peca={pilar}
          rotulo="pilar de contenção"
          base={taxaDeBaseDaPeca(catalogo, 'PILAR_CONTENCAO')}
          daPlanilha={daPlanilha}
          dispatch={dispatch}
          media={mediaDoAco}
          travadaPor={acoAprovadoDoElemento(aprovados, catalogo, 'PILAR_CONTENCAO')}
        />
      )}

      {editou && erro !== undefined && (
        <p role="alert" className="text-xs text-(--color-state-danger)">
          {erro}
        </p>
      )}

      <div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            dispatch({ type: 'REMOVE_RETAINING_COLUMN', pieceId: pilar.id });
            onFechar();
          }}
        >
          <Trash2 className="size-4" /> Excluir pilar de contenção
        </Button>
      </div>
    </section>
  );
}
